// Force-directed renderer — FXC-8538 (Warp).
//
// The first concrete `GraphRenderer`. Generalised from Bobbin's
// `DailyGraph` (FXC-8075/FXC-8109) ForceGraph2D block — same canvas
// drawing, link styling, and event wiring — so Bobbin's overlay UX is
// preserved bit-for-bit when it adopts the shared component. Canvas-
// based (~50KB gzipped), comfortable with the ~3000-node local spool
// graph in a small window; WebGL is overkill at this scale.

import { useEffect, useMemo, useRef } from "react";
import ForceGraph2D from "react-force-graph-2d";
import type { ForceGraphMethods } from "react-force-graph-2d";

import { colourFor, edgeColourFor } from "../core/palette";
import { edgeHitsHighlight } from "../core/search";
import type { GraphNode } from "../core/types";
import type { GraphControls, GraphRendererProps } from "./GraphRenderer";
import { drawGroupHull } from "./hull";
import { clusterForce } from "./clusterForce";

interface ForceNode extends GraphNode {
  _color: string;
  /** Cluster id (set when `groups` is provided) — drives the clustering force. */
  cluster?: string | number;
  // Layout-assigned coordinates (mutated by the force simulation).
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
}

interface ForceLink {
  source: string;
  target: string;
  predicate: string;
}

// react-force-graph types node ids as `string | number`; our canonical
// ids are strings. This is the shape the library hands back to event
// callbacks (it also bolts on x/y/vx/vy during layout).
type ForceCallbackNode = { id?: string | number };

/** Resolve a force-graph callback node back to its canonical id. */
function canonicalId(node: ForceCallbackNode | null): string | undefined {
  return node?.id == null ? undefined : String(node.id);
}

export function ForceRenderer({
  nodes,
  edges,
  width,
  height,
  highlightIds = null,
  searchActive = false,
  colourForType = colourFor,
  colourForPredicate = edgeColourFor,
  nodeColor,
  groups = [],
  sizeByDegree = false,
  flowParticles = false,
  onNodeClick,
  onNodeRightClick,
  onNodeHover,
  onReady,
}: GraphRendererProps) {
  const fgRef = useRef<ForceGraphMethods | undefined>(undefined);
  const onReadyRef = useRef(onReady);
  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  // id → canonical node, so event callbacks hand back the canonical
  // model rather than the layout-mutated force node.
  const byId = useMemo(() => {
    const m = new Map<string, GraphNode>();
    for (const n of nodes) m.set(n.id, n);
    return m;
  }, [nodes]);

  // node id → degree (in + out), for optional degree-scaled node radius.
  const degreeOf = useMemo(() => {
    if (!sizeByDegree) return null;
    const m = new Map<string, number>();
    for (const e of edges) {
      m.set(e.source, (m.get(e.source) ?? 0) + 1);
      m.set(e.target, (m.get(e.target) ?? 0) + 1);
    }
    return m;
  }, [edges, sizeByDegree]);

  // node id → cluster id (from the hull groups), driving the clustering force.
  const clusterOf = useMemo(() => {
    const m = new Map<string, string | number>();
    for (const g of groups) for (const id of g.nodeIds) m.set(id, g.id);
    return m;
  }, [groups]);

  // Fresh objects each time the data changes — never mutate the caller's
  // arrays (the force sim writes x/y/vx/vy onto whatever it's handed).
  const graphData = useMemo(
    () => ({
      nodes: nodes.map<ForceNode>((n) => ({
        ...n,
        _color: nodeColor?.(n) ?? colourForType(n.type),
        cluster: clusterOf.get(n.id),
      })),
      links: edges.map<ForceLink>((e) => ({
        source: e.source,
        target: e.target,
        predicate: e.predicate,
      })),
    }),
    [nodes, edges, colourForType, nodeColor, clusterOf],
  );

  // Install / remove the cluster-attraction force so clusters separate
  // spatially when grouping is active.
  useEffect(() => {
    const fg = fgRef.current as
      | { d3Force?: (name: string, f: unknown) => void; d3ReheatSimulation?: () => void }
      | undefined;
    if (!fg || typeof fg.d3Force !== "function") return;
    fg.d3Force("cluster", clusterOf.size > 0 ? clusterForce(0.14) : null);
    fg.d3ReheatSimulation?.();
  }, [clusterOf, graphData]);

  // Live id → force-node map for reading layout positions when drawing
  // cluster hulls (the objects are mutated in place by the simulation).
  const nodeObjById = useMemo(() => {
    const m = new Map<string, ForceNode>();
    for (const n of graphData.nodes) m.set(n.id, n);
    return m;
  }, [graphData]);

  // Draw cluster/clique hulls behind the nodes each frame.
  const renderFramePre = useMemo(() => {
    if (groups.length === 0) return undefined;
    return (ctx: CanvasRenderingContext2D, globalScale: number) => {
      for (const g of groups) {
        const pts: [number, number][] = [];
        for (const id of g.nodeIds) {
          const n = nodeObjById.get(id);
          if (n && n.x != null && n.y != null) pts.push([n.x, n.y]);
        }
        if (pts.length > 0) drawGroupHull(ctx, pts, g.color, globalScale);
      }
    };
  }, [groups, nodeObjById]);

  const readyControls = useMemo<GraphControls>(
    () => ({
      zoomToFit: (durationMs = 400, padding = 40) =>
        fgRef.current?.zoomToFit(durationMs, padding),
    }),
    [],
  );
  const hasOnReady = onReady !== undefined;

  // Expose imperative controls (zoom-to-fit) to the host.
  useEffect(() => {
    if (!hasOnReady) return;
    onReadyRef.current?.(readyControls);
    return () => onReadyRef.current?.(null);
  }, [hasOnReady, readyControls]);

  return (
    <ForceGraph2D
      ref={fgRef}
      width={width}
      height={height}
      graphData={graphData}
      nodeRelSize={3}
      linkDirectionalArrowLength={(l: ForceLink) =>
        l.predicate === "child_of" ? 0 : 3
      }
      linkDirectionalArrowRelPos={1}
      linkColor={(l: ForceLink) => colourForPredicate(l.predicate)}
      linkWidth={(l: ForceLink) =>
        searchActive && edgeHitsHighlight(l, highlightIds) ? 1.6 : 0.6
      }
      linkDirectionalParticles={(l: ForceLink) =>
        flowParticles && edgeHitsHighlight(l, highlightIds) ? 4 : 0
      }
      linkDirectionalParticleWidth={2}
      linkDirectionalParticleSpeed={0.006}
      backgroundColor="rgba(0,0,0,0)"
      cooldownTicks={120}
      onRenderFramePre={renderFramePre}
      onNodeClick={(node: ForceCallbackNode) => {
        const id = canonicalId(node);
        const canonical = id ? byId.get(id) : undefined;
        if (canonical) onNodeClick?.(canonical);
      }}
      onNodeRightClick={(node: ForceCallbackNode) => {
        const id = canonicalId(node);
        const canonical = id ? byId.get(id) : undefined;
        if (canonical) onNodeRightClick?.(canonical);
      }}
      onNodeHover={(node: ForceCallbackNode | null) => {
        const id = canonicalId(node);
        const canonical = id ? byId.get(id) : undefined;
        onNodeHover?.(canonical ?? null);
      }}
      nodeCanvasObjectMode={() => "after"}
      nodeCanvasObject={(rawNode: ForceCallbackNode, ctx: CanvasRenderingContext2D, scale: number) => {
        const node = rawNode as ForceNode;
        const label = String(node.label ?? "");
        const colour = node._color ?? colourForType(node.type ?? "");
        const isHighlight = highlightIds?.has(node.id) ?? false;
        const base = degreeOf
          ? Math.min(9, 2.5 + Math.sqrt(degreeOf.get(node.id) ?? 0) * 1.3)
          : 3;
        const radius = isHighlight ? base + 2 : base;
        ctx.beginPath();
        ctx.arc(node.x ?? 0, node.y ?? 0, radius, 0, Math.PI * 2);
        ctx.fillStyle = colour;
        ctx.fill();
        if (isHighlight) {
          ctx.lineWidth = 1.2;
          ctx.strokeStyle = "rgba(255,255,255,0.9)";
          ctx.stroke();
        }
        if (scale > 1.4 || isHighlight) {
          ctx.font = `${10 / scale}px ui-monospace, monospace`;
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          ctx.fillStyle = isHighlight
            ? "rgba(255,255,255,0.95)"
            : "rgba(255,255,255,0.7)";
          const text = label.length > 28 ? `${label.slice(0, 28)}…` : label;
          ctx.fillText(text, (node.x ?? 0) + radius + 2, node.y ?? 0);
        }
      }}
    />
  );
}
